import type { FormApi, SubmissionErrors } from 'final-form';
import React, { useCallback } from 'react';

import createForm from '@swf/components/Forms/Form';
import { StatusCode } from '@swf/core/enums/statusCode';
import useRequest from '@swf/core/hooks/useRequest';
import type { ErrorResponse } from '@swf/core/types/Response';
import { buildFormErrors } from '@swf/core/utils/forms';

import useStateReducer, { DispatchActionType } from '../../../hooks/useStateReducer';
import type { ExcludePlayerResponse } from '../../shared/definitions';
import { type FormValues } from '../definitions';

import ExclusionRevocationFormContent from './ExclusionRevocationFormContent/ExclusionRevocationFormContent';

const initialValues = {
  password: ''
};

interface Props {
  formattedEndDate: JSX.Element;
}

const ExclusionRevocationForm: React.FC<Props> = ({ formattedEndDate }) => {
  const Form = createForm<FormValues>();
  const request = useRequest();

  const [{ showConfirm, invalidPassword, response, error }, dispatchAction] =
    useStateReducer<ExcludePlayerResponse>(false);

  const handleCancel = useCallback(
    (formApi: FormApi<FormValues>) => {
      formApi.reset();
      dispatchAction({ type: DispatchActionType.Reset });
    },
    [dispatchAction]
  );
  const handleResponse = useCallback(
    (
      body: ExcludePlayerResponse,
      initialize: (data: FormValues | ((values: FormValues) => FormValues)) => void,
      callback?: (errors?: SubmissionErrors) => void
    ) => {
      callback?.();
      initialize(initialValues);
      dispatchAction({ type: DispatchActionType.Response, payload: body });
    },
    [dispatchAction]
  );

  const handleError = useCallback(
    (
      { headers: { statusCode }, body }: ErrorResponse,
      callback?: (errors?: SubmissionErrors) => void
    ) => {
      callback?.();
      const errors = buildFormErrors({ success: false, errors: body });
      const isPasswordInvalid =
        statusCode === StatusCode.WRONG_PWD || errors?.invalidParameter === 'password';
      if (isPasswordInvalid) {
        dispatchAction({ type: DispatchActionType.InvalidPassword });
      } else {
        dispatchAction({ type: DispatchActionType.Error });
      }
    },
    [dispatchAction]
  );

  const handleSubmit = useCallback(
    (
      { password }: FormValues,
      { initialize }: FormApi<FormValues>,
      callback?: (errors?: SubmissionErrors) => void
    ) => {
      request({
        service: 'responsiblegaming.revokeselfexclusion',
        body: { password }
      }).subscribe(
        ({ body }) => handleResponse(body, initialize, callback),
        (error) => handleError(error, callback)
      );
    },
    [handleError, handleResponse, request]
  );

  const handleConfirm = useCallback(
    (formApi: FormApi<FormValues>) => {
      formApi.change('password');
      dispatchAction({ type: DispatchActionType.ShowConfirm });
    },
    [dispatchAction]
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        ...initialValues
      }}
      render={({ form, pristine }) => (
        <ExclusionRevocationFormContent
          hasError={error}
          submittedRevocationTime={response?.revocationTimeUntil}
          showConfirm={showConfirm}
          isPristine={pristine}
          handleCancelForm={() => handleCancel(form)}
          handleConfirmForm={() => handleConfirm(form)}
          invalidPassword={invalidPassword}
          formattedEndDate={formattedEndDate}
        />
      )}
    />
  );
};

export default ExclusionRevocationForm;
